import React, { useState, useEffect, useCallback } from "react";
import { debounce } from "lodash";
import { colors } from '../../../theme/colors';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  MenuItem,
  Popover,
  Checkbox,
  ListItemText,
  IconButton,
  TextField,
  Paper,
  InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../utils/axiosInstance";
import { JobEndPoint, UserAuthenticationEndPoint } from "../../../api-routes";
import useGlobalContext from "../../../contexts-hooks/hooks/useGlobalContext";
import { MainWrapper } from "./style";

const statusColors = {
  Active: { bg: "#e6f4ea", color: "#1b7a3d" },
  Paused: { bg: "#fef3e2", color: "#b45309" },
  Closed: { bg: "#e8eef5", color: colors.primary },
  Cancelled: { bg: "#fce8e8", color: "#d32f2f" },
};

const Jobs = () => {
  const navigate = useNavigate();
  const { state } = useGlobalContext();
  const isAdmin = state?.user?.role === "admin";
  const currentUserId = state?.user?._id || "";
  const [search, setSearch] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(5);
  const [loading, setLoading] = useState(true);

  const isAccountManager = jobs.some(
    (job) => job.accountManager === currentUserId,
  );

  const [statusFilter, setStatusFilter] = useState("");
  const [deadlineFrom, setDeadlineFrom] = useState("");
  const [deadlineTo, setDeadlineTo] = useState("");
  const [tempFrom, setTempFrom] = useState("");
  const [tempTo, setTempTo] = useState("");
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [dateAnchorEl, setDateAnchorEl] = useState(null);

  const [recruiterList, setRecruiterList] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  const activeFilterCount =
    (searchValue ? 1 : 0) +
    (statusFilter ? 1 : 0) +
    (deadlineFrom || deadlineTo ? 1 : 0);

  const handleResetFilters = () => {
    setSearchValue("");
    setSearch("");
    setStatusFilter("");
    setDeadlineFrom("");
    setDeadlineTo("");
    setTempFrom("");
    setTempTo("");
    setPage(1);
  };

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      let params = `?page=${page}&limit=${limit}&search=${search}`;
      if (statusFilter) params += `&status=${statusFilter}`;
      if (deadlineFrom) params += `&deadlineFrom=${deadlineFrom}`;
      if (deadlineTo) params += `&deadlineTo=${deadlineTo}`;
      const res = await axiosInstance.get(JobEndPoint.GET_ALL_JOBS + params);
      setJobs(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      console.error("Failed to fetch jobs", err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, statusFilter, deadlineFrom, deadlineTo]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, deadlineFrom, deadlineTo]);

  useEffect(() => {
    if (!isAdmin) {
      const fetchRecruiters = async () => {
        try {
          const res = await axiosInstance.get(
            UserAuthenticationEndPoint.GET_ALL_USERS +
              "?page=1&limit=100&search=",
          );
          setRecruiterList(res.data.data || []);
        } catch (err) {
          console.error("Failed to fetch recruiters", err);
        }
      };
      fetchRecruiters();
    }
  }, [isAdmin]);

  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPage(1);
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);
    debouncedSearch(value);
  };

  const handleOpenAssign = (event, job) => {
    setAnchorEl(event.currentTarget);
    setActiveJob(job);
    setSelectedUserIds(job.assignedUsers || []);
  };

  const handleCloseAssign = async () => {
    if (activeJob) {
      try {
        await axiosInstance.post(JobEndPoint.ASSIGN_USERS, {
          jobId: activeJob._id,
          userIds: selectedUserIds,
        });
        fetchJobs();
      } catch (err) {
        console.error("Failed to assign users", err);
      }
    }
    setAnchorEl(null);
    setActiveJob(null);
  };

  const handleToggleUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatBudget = (budget) => {
    if (!budget) return "—";
    const num = Number(budget);
    if (num >= 100000) {
      return `₹${(num / 100000).toFixed(1)}L`;
    }
    if (num >= 1000) {
      return `₹${(num / 1000).toFixed(0)}k`;
    }
    return `₹${num}`;
  };

  const Budget = (value) => {
    return value != null ? `${Number(value)} LPA` : "—";
  };

  const totalPages = Math.ceil(total / limit);

  const SkeletonRow = () => (
    <TableRow>
      {Array.from({ length: 7 }).map((_, i) => (
        <TableCell key={i}>
          <Skeleton variant="text" width={i === 0 ? 100 : 120} height={30} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <MainWrapper>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: "16px",
          backgroundColor: "#ffffff",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.03)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
            pb: 2,
            mb: 2,
            borderBottom: "1px solid #f1f5f9",
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1e293b",
                  letterSpacing: "-0.5px",
                  fontSize: "1.35rem",
                }}
              >
                Jobs
              </Typography>
              <Chip
                label={`${total} Jobs`}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  backgroundColor: colors.primaryLight,
                  color: colors.primary,
                  borderRadius: "6px",
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
              Manage and track active recruitment requisitions.
            </Typography>
          </Box>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/app/jobs/create")}
              sx={{
                borderRadius: "10px",
                backgroundColor: colors.primary,
                textTransform: "none",
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.875rem",
                px: 2.5,
                py: "8px",
                boxShadow: `0 4px 14px ${colors.primary}40`,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: colors.primaryHover,
                  boxShadow: `0 6px 18px ${colors.primary}60`,
                },
              }}
            >
              Create New Job
            </Button>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              minWidth: { xs: "100%", md: "auto" },
            }}
          >
            <TextField
              size="small"
              placeholder="Search by Job ID, Role, or Client..."
              value={searchValue}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b", fontSize: "1.1rem" }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleSearchChange({ target: { value: "" } })}
                      edge="end"
                    >
                      <ClearIcon sx={{ fontSize: "0.95rem" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                minWidth: { xs: "100%", sm: "280px" },
                flex: { sm: 1, md: "0 1 340px" },
                "& .MuiOutlinedInput-root": {
                  height: "38px",
                  borderRadius: "10px",
                  fontSize: "0.85rem",
                  backgroundColor: searchValue ? colors.primaryLight : "#ffffff",
                  "& fieldset": {
                    borderColor: searchValue ? colors.primaryLightBorder : "#cbd5e1",
                  },
                  "&:hover fieldset": {
                    borderColor: colors.primary,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: colors.primary,
                    borderWidth: "1.5px",
                  },
                },
              }}
            />

            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon sx={{ fontSize: "1.05rem" }} />}
              onClick={(e) => setStatusAnchorEl(e.currentTarget)}
              sx={{
                height: "38px",
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                backgroundColor: statusFilter ? colors.primaryLight : "#ffffff",
                borderColor: statusFilter ? colors.primaryLightBorder : "#cbd5e1",
                color: statusFilter ? colors.primary : "#334155",
                px: 2,
                "&:hover": {
                  borderColor: colors.primary,
                  backgroundColor: statusFilter ? colors.primaryLight : "#f8fafc",
                },
              }}
            >
              {statusFilter ? `Status: ${statusFilter}` : "Status"}
            </Button>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DateRangeIcon sx={{ fontSize: "1.05rem" }} />}
              onClick={(e) => {
                setTempFrom(deadlineFrom);
                setTempTo(deadlineTo);
                setDateAnchorEl(e.currentTarget);
              }}
              sx={{
                height: "38px",
                borderRadius: "10px",
                textTransform: "none",
                fontSize: "0.85rem",
                fontWeight: 500,
                backgroundColor: (deadlineFrom || deadlineTo) ? colors.primaryLight : "#ffffff",
                borderColor: (deadlineFrom || deadlineTo) ? colors.primaryLightBorder : "#cbd5e1",
                color: (deadlineFrom || deadlineTo) ? colors.primary : "#334155",
                px: 2,
                "&:hover": {
                  borderColor: colors.primary,
                  backgroundColor: (deadlineFrom || deadlineTo) ? colors.primaryLight : "#f8fafc",
                },
              }}
            >
              {(deadlineFrom || deadlineTo)
                ? `${deadlineFrom || "..."} - ${deadlineTo || "..."}`
                : "Date Range"}
            </Button>
          </Box>

          {activeFilterCount > 0 && (
            <Button
              size="small"
              onClick={handleResetFilters}
              startIcon={<RotateLeftIcon sx={{ fontSize: "1rem" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                fontWeight: 600,
                color: colors.primary,
                backgroundColor: colors.primaryLight,
                border: `1px solid ${colors.primaryLightBorder}`,
                borderRadius: "8px",
                px: 1.5,
                py: "6px",
                height: "38px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: colors.sidebarSelected || colors.primaryLight,
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                },
              }}
            >
              Clear {activeFilterCount} {activeFilterCount === 1 ? "Filter" : "Filters"}
            </Button>
          )}
        </Box>
      </Paper>

      <TableContainer className="table-container">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>JOB ID</TableCell>
              <TableCell>ROLE/DESIGNATION</TableCell>
              <TableCell>COMPANY/CLIENT</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>BUDGET</TableCell>
              <TableCell>DEADLINE</TableCell>
              {isAdmin && <TableCell>ACCOUNT MANAGER</TableCell>}
              {!isAdmin && isAccountManager && (
                <TableCell align="center">ACTIONS</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: limit }).map((_, i) => (
                <SkeletonRow key={`skeleton-${i}`} />
              ))
            ) : jobs.length > 0 ? (
              jobs.map((job) => (
                <TableRow
                  key={job._id}
                  onClick={() => navigate(`/app/jobs/${job._id}`)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#f5f8fa" },
                  }}
                >
                  <TableCell className="job-id-cell">{job.jobId}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography className="role-text">
                        {job.roleDesignation}
                      </Typography>
                      <Typography className="role-subtext">
                        {job.employmentType}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{job.company}</TableCell>
                  <TableCell>
                    <Chip
                      label={job.status}
                      size="small"
                      sx={{
                        backgroundColor:
                          statusColors[job.status]?.bg || "#e8eef5",
                        color: statusColors[job.status]?.color || colors.primary,
                        fontWeight: 600,
                        fontSize: "12px",
                      }}
                    />
                  </TableCell>
                  <TableCell>{Budget(job.budget)}</TableCell>{" "}
                  <TableCell>{formatDate(job.deadline)}</TableCell>
                  {isAdmin && (
                    <TableCell>{job.accountManagerName || "—"}</TableCell>
                  )}
                  {!isAdmin && isAccountManager && (
                    <TableCell align="center">
                      {job.accountManager === currentUserId ? (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenAssign(e, job);
                          }}
                          className="assign-btn"
                        >
                          <PersonAddIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={isAdmin ? 7 : !isAdmin && isAccountManager ? 7 : 6}
                  align="center"
                  sx={{ py: 8 }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                    <BusinessCenterIcon sx={{ fontSize: 56, color: "#d1d5db" }} />
                    <Typography variant="h6" fontWeight={600} color="text.secondary">
                      No jobs found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your search or filters, or create a new job.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box className="pagination-container">
        <Typography className="pagination-info">
          Showing {jobs.length > 0 ? (page - 1) * limit + 1 : 0} to{" "}
          {Math.min(page * limit, total)} of {total} entries
        </Typography>
        <Box className="pagination-buttons">
          <Button
            className="btn"
            variant="contained"
            onClick={() => setPage((p) => p - 1)}
            disabled={page === 1 || loading}
            startIcon={<NavigateBeforeIcon />}
          >
            Previous
          </Button>
          <Button
            className="btn"
            variant="contained"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages || loading}
            endIcon={<NavigateNextIcon />}
          >
            Next
          </Button>
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseAssign}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 220,
            maxHeight: 250,
            overflow: "auto",
            boxShadow: "0 8px 24px rgba(127, 29, 29, 0.15)",
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              color: "#637381",
              px: 1,
              py: 0.5,
            }}
          >
            Assign Recruiters
          </Typography>
          {recruiterList.map((user) => (
            <MenuItem
              key={user._id}
              onClick={() => handleToggleUser(user._id)}
              sx={{
                borderRadius: "8px",
                py: 0.5,
                fontSize: "14px",
              }}
            >
              <Checkbox
                size="small"
                checked={selectedUserIds.includes(user._id)}
                sx={{
                  color: colors.primary,
                  "&.Mui-checked": { color: colors.primary },
                  p: 0,
                  mr: 1,
                }}
              />
              <ListItemText
                primary={user.name}
                primaryTypographyProps={{ fontSize: "13px" }}
              />
            </MenuItem>
          ))}
        </Box>
      </Popover>

      <Popover
        open={Boolean(statusAnchorEl)}
        anchorEl={statusAnchorEl}
        onClose={() => setStatusAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 160,
            boxShadow: "0 8px 24px rgba(127, 29, 29, 0.15)",
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          {["All", "Active", "Paused", "Closed", "Cancelled"].map((s) => (
            <MenuItem
              key={s}
              selected={s === "All" ? !statusFilter : statusFilter === s}
              onClick={() => {
                setStatusFilter(s === "All" ? "" : s);
                setStatusAnchorEl(null);
              }}
              sx={{ borderRadius: "8px", fontSize: "14px" }}
            >
              {s}
            </MenuItem>
          ))}
        </Box>
      </Popover>

      <Popover
        open={Boolean(dateAnchorEl)}
        anchorEl={dateAnchorEl}
        onClose={() => setDateAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            minWidth: 260,
            boxShadow: "0 8px 24px rgba(127, 29, 29, 0.15)",
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography
            sx={{ fontSize: "13px", fontWeight: 600, color: "#637381" }}
          >
            Filter by Deadline
          </Typography>
          <TextField
            label="From"
            type="date"
            size="small"
            value={tempFrom}
            onChange={(e) => setTempFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={tempTo}
            onChange={(e) => setTempTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!deadlineFrom && !deadlineTo}
              onClick={() => {
                setTempFrom("");
                setTempTo("");
                setDeadlineFrom("");
                setDeadlineTo("");
                setDateAnchorEl(null);
              }}
              sx={{
                flex: 1,
                textTransform: "none",
                color: colors.primary,
                borderColor: colors.primary,
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={!tempFrom && !tempTo}
              onClick={() => {
                setDeadlineFrom(tempFrom);
                setDeadlineTo(tempTo);
                setDateAnchorEl(null);
              }}
              sx={{
                flex: 1,
                backgroundColor: colors.primary,
                textTransform: "none",
                "&:hover": { backgroundColor: colors.primaryHover },
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </MainWrapper>
  );
};

export default Jobs;
